import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { BookOpen, Clock, Upload, Menu, ChevronDown, Link } from 'lucide-react';
import CompetitionAnalysis from '../CompetitionAnalysis';
import EventEnrollments from '../EventEnrollments';
import { useDashboard } from './DashboardContext';

export const FormatoreDashboard = () => {
  const {
    user, activeRole,
    activeFormatoreTab, setActiveFormatoreTab,
    formatoreMobileMenuOpen, setFormatoreMobileMenuOpen,
    teacherEvents,
    fetchFormatoreEventEnrollments,
    fetchStudyMaterials, setSelectedEventForMaterials,
    setOnlineMeetingLinks, setSelectedEventForLinks,
    formatDate, formatDateRange
  } = useDashboard();

  return (
    <Tabs value={activeFormatoreTab} onValueChange={setActiveFormatoreTab} className="space-y-6" data-testid="formatore-dashboard">
      <TabsList className="hidden md:grid w-full grid-cols-3">
        <TabsTrigger value="competition" data-testid="formatore-competition-tab">Analysis di Gara</TabsTrigger>
        <TabsTrigger value="events" data-testid="formatore-events-tab">I Miei Eventi Formativi</TabsTrigger>
        <TabsTrigger value="materials" data-testid="formatore-materials-tab">Materiale Didattico</TabsTrigger>
      </TabsList>
      
      {/* Mobile Menu */}
      <div className="md:hidden relative">
        <button
          onClick={() => setFormatoreMobileMenuOpen(!formatoreMobileMenuOpen)}
          className="w-full flex items-center justify-between px-4 py-3 bg-white border rounded-lg shadow-sm"
          data-testid="formatore-mobile-menu-button"
        >
          <div className="flex items-center space-x-2">
            <Menu className="w-5 h-5 text-gray-600" />
            <span className="text-gray-600">Scegli sezione</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="font-medium text-red-600">
              {activeFormatoreTab === 'competition' && 'Analysis di Gara'}
              {activeFormatoreTab === 'events' && 'I Miei Eventi Formativi'}
              {activeFormatoreTab === 'materials' && 'Materiale Didattico'}
            </span>
            <ChevronDown className={`w-5 h-5 text-gray-600 transition-transform ${formatoreMobileMenuOpen ? 'rotate-180' : ''}`} />
          </div>
        </button>
        
        {formatoreMobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-50">
            {[
              { value: 'competition', label: 'Analysis di Gara' },
              { value: 'events', label: 'I Miei Eventi Formativi' },
              { value: 'materials', label: 'Materiale Didattico' }
            ].map((item) => (
              <button
                key={item.value}
                onClick={() => {
                  setActiveFormatoreTab(item.value);
                  setFormatoreMobileMenuOpen(false);
                }}
                className={`w-full text-left px-4 py-3 hover:bg-gray-50 border-b last:border-b-0 ${
                  activeFormatoreTab === item.value ? 'bg-red-50 text-red-600 font-medium' : 'text-gray-700'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
      </div>
      
      <TabsContent value="competition" data-testid="formatore-competition-analysis-content">
        <CompetitionAnalysis user={user} activeRole={activeRole} />
      </TabsContent>
      
      <TabsContent value="events" data-testid="formatore-events-content">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <BookOpen className="w-5 h-5" />
              <span>I Miei Eventi di Formazione Insegnanti</span>
            </CardTitle>
            <CardDescription>Eventi formativi che gestisci per gli insegnanti</CardDescription>
          </CardHeader>
          <CardContent>
            {teacherEvents.length === 0 ? (
              <p className="text-gray-500 text-center py-8" data-testid="no-formatore-events">
                Non hai ancora eventi formativi assegnati
              </p>
            ) : (
              <div className="space-y-4" data-testid="formatore-events-list">
                {teacherEvents.map((event) => (
                  <div key={event.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors" data-testid={`formatore-event-${event.id}`}>
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg" data-testid={`formatore-event-title-${event.id}`}>{event.title}</h3>
                        <p className="text-gray-600 mb-2" data-testid={`formatore-event-description-${event.id}`}>{event.description}</p>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span data-testid={`formatore-event-date-${event.id}`}>{formatDateRange(event.start_date, event.end_date, event.date_tbd)}</span>
                          <Badge variant="secondary" data-testid={`formatore-event-type-${event.id}`}>Formazione Insegnanti</Badge>
                          <span data-testid={`formatore-event-enrolled-${event.id}`}>
                            {event.enrolled_count} partecipanti
                          </span>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <EventEnrollments 
                          eventId={event.id}
                          eventTitle={event.title}
                          fetchEnrollments={fetchFormatoreEventEnrollments}
                          userRole="formatore"
                        />
                        <Button
                          variant="outline" size="sm"
                          className="text-purple-600 border-purple-200 hover:bg-purple-50"
                          onClick={async () => {
                            await fetchStudyMaterials(event.id);
                            setSelectedEventForMaterials(event);
                          }}
                          data-testid={`formatore-study-materials-${event.id}`}
                        >
                          <BookOpen className="w-4 h-4 mr-1" />
                          Materiale Didattico
                        </Button>
                        <Button
                          variant="outline" size="sm"
                          className="text-purple-600 border-purple-200 hover:bg-purple-50"
                          onClick={() => {
                            setOnlineMeetingLinks({
                              online_meeting_link_1: event.online_meeting_link_1 || '',
                              online_meeting_datetime_1: event.online_meeting_datetime_1 ? new Date(event.online_meeting_datetime_1).toISOString().slice(0, 16) : '',
                              online_meeting_link_2: event.online_meeting_link_2 || '',
                              online_meeting_datetime_2: event.online_meeting_datetime_2 ? new Date(event.online_meeting_datetime_2).toISOString().slice(0, 16) : '',
                              online_meeting_link_3: event.online_meeting_link_3 || '',
                              online_meeting_datetime_3: event.online_meeting_datetime_3 ? new Date(event.online_meeting_datetime_3).toISOString().slice(0, 16) : '',
                              online_meeting_link_4: event.online_meeting_link_4 || '',
                              online_meeting_datetime_4: event.online_meeting_datetime_4 ? new Date(event.online_meeting_datetime_4).toISOString().slice(0, 16) : ''
                            });
                            setSelectedEventForLinks(event);
                          }}
                          data-testid={`formatore-meeting-links-${event.id}`}
                        >
                          <Link className="w-4 h-4 mr-1" />
                          Incontri Online
                        </Button>
                      </div>
                    </div>
                    
                    {/* Display existing meeting links */}
                    {(event.online_meeting_link_1 || event.online_meeting_link_2 || event.online_meeting_link_3 || event.online_meeting_link_4) && (
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-sm font-medium text-gray-700 mb-2">Link Incontri Online:</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {[1, 2, 3, 4].map(n => {
                            const link = event[`online_meeting_link_${n}`];
                            const dt = event[`online_meeting_datetime_${n}`];
                            if (!link) return null;
                            return (
                              <div key={n} className="flex flex-col">
                                <a href={link} target="_blank" rel="noopener noreferrer" 
                                   className="text-sm text-purple-600 hover:text-purple-800 hover:underline flex items-center font-medium">
                                  <Link className="w-3 h-3 mr-1" />
                                  Incontro Online {n}
                                </a>
                                {dt && (
                                  <span className="text-xs text-gray-500 ml-4 mt-1">
                                    <Clock className="w-3 h-3 inline mr-1" />
                                    {formatDate(dt)}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>
      
      <TabsContent value="materials" data-testid="formatore-materials-content">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Upload className="w-5 h-5" />
              <span>Gestione Materiale Didattico</span>
            </CardTitle>
            <CardDescription>Carica e gestisci materiali per i tuoi eventi formativi</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-gray-500 text-center py-8">
              Seleziona un evento dalla tab "I Miei Eventi Formativi" per gestire il materiale didattico
            </p>
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
};
